/**
 * Currency utilities for localized pricing support
 * Handles currency formatting, conversion, and locale-aware displays
 */

export const CURRENCIES = {
  USD: {
    code: 'USD',
    symbol: '$',
    position: 'before', // $ 1,234.56
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    name: 'US Dollar'
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    position: 'after', // 1.234,56 €
    decimalPlaces: 2,
    thousandSeparator: ' ',
    decimalSeparator: ',',
    name: 'Euro'
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    position: 'before', // £ 1,234.56
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    name: 'British Pound'
  },
  ZAR: {
    code: 'ZAR',
    symbol: 'R',
    position: 'before', // R 1,234.56
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    name: 'South African Rand'
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    position: 'before', // C$ 1,234.56
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    name: 'Canadian Dollar'
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    position: 'before', // A$ 1,234.56
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    name: 'Australian Dollar'
  },
  JPY: {
    code: 'JPY',
    symbol: '¥',
    position: 'before', // ¥ 123,456
    decimalPlaces: 0, // JPY doesn't use decimals
    thousandSeparator: ',',
    decimalSeparator: '.',
    name: 'Japanese Yen'
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    position: 'before', // ₹ 1,23,456.78
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    name: 'Indian Rupee'
  },
  CHF: {
    code: 'CHF',
    symbol: 'CHF',
    position: 'before', // CHF 1,234.56
    decimalPlaces: 2,
    thousandSeparator: "'",
    decimalSeparator: '.',
    name: 'Swiss Franc'
  },
  SEK: {
    code: 'SEK',
    symbol: 'kr',
    position: 'after', // 1,234.56 kr
    decimalPlaces: 2,
    thousandSeparator: ' ',
    decimalSeparator: ',',
    name: 'Swedish Krona'
  },
  NZD: {
    code: 'NZD',
    symbol: 'NZ$',
    position: 'before', // NZ$ 1,234.56
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    name: 'New Zealand Dollar'
  },
  SGD: {
    code: 'SGD',
    symbol: 'S$',
    position: 'before', // S$ 1,234.56
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    name: 'Singapore Dollar'
  }
};

/**
 * Format a numeric amount as localized currency string
 * @param {number} amount - Numeric amount to format
 * @param {string} currency - Currency code (default: USD)
 * @param {boolean} includeSymbol - Whether to include currency symbol (default: true)
 * @returns {string} Formatted currency string
 * 
 * Examples:
 *   formatCurrency(1234.56, 'USD') => '$1,234.56'
 *   formatCurrency(1234.56, 'EUR') => '1.234,56 €'
 *   formatCurrency(123456, 'JPY') => '¥123,456'
 */
export function formatCurrency(amount, currency = 'USD', includeSymbol = true) {
  try {
    const currencyCode = String(currency).toUpperCase();
    const config = CURRENCIES[currencyCode] || CURRENCIES.USD;
    
    const { decimalPlaces, thousandSeparator, decimalSeparator, symbol, position } = config;
    
    // Handle null/undefined amounts
    if (amount === null || amount === undefined) {
      return includeSymbol ? `${symbol} 0` : '0';
    }
    
    // Convert to number and round
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
      return includeSymbol ? `${symbol} 0` : '0';
    }
    
    const rounded = numAmount.toFixed(decimalPlaces);
    
    // Split into integer and decimal parts
    const [integerPart, decimalPart] = rounded.split('.');
    
    // Add thousand separators to integer part
    let formattedInteger = '';
    if (thousandSeparator === ',') {
      formattedInteger = parseInt(integerPart).toLocaleString('en-US');
    } else if (thousandSeparator === ' ') {
      formattedInteger = parseInt(integerPart).toLocaleString('de-DE');
    } else if (thousandSeparator === "'") {
      formattedInteger = parseInt(integerPart).toLocaleString('de-CH');
    } else {
      formattedInteger = integerPart;
    }
    
    // Build the number string
    let numberStr = formattedInteger;
    if (decimalPlaces > 0 && decimalPart) {
      numberStr += decimalSeparator + decimalPart.substring(0, decimalPlaces);
    }
    
    // Add currency symbol
    if (includeSymbol) {
      if (position === 'before') {
        return `${symbol} ${numberStr}`;
      } else {
        return `${numberStr} ${symbol}`;
      }
    }
    
    return numberStr;
  } catch (error) {
    console.warn('Error formatting currency:', error);
    return String(amount);
  }
}

/**
 * Get the currency symbol for a given currency code
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} Currency symbol
 */
export function getCurrencySymbol(currency = 'USD') {
  const config = CURRENCIES[String(currency).toUpperCase()] || CURRENCIES.USD;
  return config.symbol;
}

/**
 * Get the number of decimal places for a currency
 * @param {string} currency - Currency code (default: USD)
 * @returns {number} Decimal places
 */
export function getDecimalPlaces(currency = 'USD') {
  const config = CURRENCIES[String(currency).toUpperCase()] || CURRENCIES.USD;
  return config.decimalPlaces;
}

/**
 * Get list of all supported currencies
 * @returns {Array} List of currency objects with code, symbol, and name
 */
export function getCurrencyList() {
  return Object.values(CURRENCIES).map(currency => ({
    code: currency.code,
    symbol: currency.symbol,
    name: currency.name
  }));
}

/**
 * Detect user's locale and suggest a default currency
 * @returns {string} Suggested currency code
 */
export function detectDefaultCurrency() {
  try {
    // Try to get from browser locale
    const locale = navigator.language || navigator.userLanguage;
    
    // Map locale to currency (simplified mapping)
    const currencyMap = {
      'en-US': 'USD',
      'de-DE': 'EUR',
      'de-AT': 'EUR',
      'de-CH': 'CHF',
      'fr-FR': 'EUR',
      'fr-CH': 'CHF',
      'it-IT': 'EUR',
      'es-ES': 'EUR',
      'pt-PT': 'EUR',
      'pt-BR': 'BRL',
      'ja-JP': 'JPY',
      'zh-CN': 'CNY',
      'en-GB': 'GBP',
      'en-AU': 'AUD',
      'en-NZ': 'NZD',
      'en-CA': 'CAD',
      'en-SG': 'SGD',
      'hi-IN': 'INR',
      'af-ZA': 'ZAR',
      'sv-SE': 'SEK',
    };
    
    return currencyMap[locale] || 'USD';
  } catch {
    return 'USD';
  }
}

/**
 * Parse a localized currency string back to number
 * @param {string} currencyString - Formatted currency string
 * @param {string} currency - Currency code
 * @returns {number} Parsed numeric value
 */
export function parseCurrency(currencyString, currency = 'USD') {
  try {
    const config = CURRENCIES[String(currency).toUpperCase()] || CURRENCIES.USD;
    
    // Remove currency symbol
    let cleaned = currencyString.replace(config.symbol, '').trim();
    
    // Replace thousand and decimal separators
    cleaned = cleaned.replace(new RegExp(`\\${config.thousandSeparator}`, 'g'), '');
    if (config.decimalSeparator !== '.') {
      cleaned = cleaned.replace(config.decimalSeparator, '.');
    }
    
    return parseFloat(cleaned);
  } catch (error) {
    console.warn('Error parsing currency:', error);
    return 0;
  }
}
